import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Checkbox } from 'antd';
import AntdCustomButton from '../buttons/AntdCustomButton';
import { SmallText } from '../ui/Typography';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, MinusCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const EditPageModal = ({
  visible,
  onCancel,
  onSave,
  initialData,
  mode = 'edit'
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && initialData) {
      form.setFieldsValue({
        pageName: initialData.pageName,
        customKeywords: initialData.customKeywords || [],
        description: initialData.description || '',
        regenerate: initialData.isNew
          ? false
          : initialData.shouldRegenerateContent ?? true
      });
    }
  }, [visible, initialData, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave({
        id: initialData?.id,
        pageName: values.pageName,
        description: values.description,
        parentPageId: initialData?.parentPageId,
        customKeywords: values.customKeywords,
        regenerate: values.regenerate
      });
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={mode === 'edit' ? t('Edit page') : t('Add page')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={600}
      centered
      className="edit-page-modal">
      <Form form={form} layout="vertical" className="mt-4">
        {/* Seitenname */}
        <Form.Item
          label={<span className="text-sm font-medium">{t('Page name')}</span>}
          name="pageName"
          rules={[{ required: true, message: t('Please enter page name') }]}>
          <Input
            placeholder={t('Startseite')}
            disabled={initialData?.isDefault}
            className="rounded-xl px-4 py-2.5 text-sm"
          />
        </Form.Item>

        {/* Keywords Container */}
        <div
          className="rounded-[4px] p-4 mb-6"
          style={{ border: '1px solid rgba(0, 0, 0, 0.15)' }}>
          <div className="text-base font-semibold mb-3">
            {t('Keywords for this page')}
          </div>

          <Form.Item name="customKeywords" className="mb-0">
            <Select
              mode="tags"
              placeholder=""
              tokenSeparators={[',']}
              className="sitemap-keywords bg-[#EEFBFF] radius-[12px]"
              dropdownStyle={{ display: 'none' }}
              showSearch={false}
              suffixIcon={false}
              tagRender={(props) => {
                const { label, onClose } = props;
                return (
                  <span
                    className="inline-flex items-center bg-[#EEFBFF] border border-solid border-[rgba(0,0,0,0.15)] mr-[3px] px-[8px] py-[1px]"
                    style={{
                      fontSize: 12,
                      fontStyle: 'normal',
                      fontWeight: 400,
                      color: 'var(--colorText, rgba(0, 0, 0, 0.88))'
                    }}>
                    <MinusCircleOutlined
                      onClick={onClose}
                      className="text-[14px] mr-2 cursor-pointer"
                    />
                    {label}
                  </span>
                );
              }}
            />
          </Form.Item>

          <div className="text-sm text-gray-600 mt-2">
            {t('Press [Enter] to add the keyword')}
          </div>
        </div>

        {/* Beschreibung */}
        <Form.Item
          label={
            <span className="text-sm font-medium">
              {t('Description')}{' '}
              <span className="text-gray-400">(optional)</span>
            </span>
          }
          name="description">
          <TextArea
            rows={3}
            placeholder={t('A short description of the page')}
            className="resize-none rounded-xl px-4 py-2.5 text-sm"
          />
        </Form.Item>
        
        <SmallText style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginTop: '-16px', marginBottom: '16px' }}>
          {t(
            'Keywords and description will be used to generate content for this page. If you edit them, the entire page content will be regenerated.'
          )}
        </SmallText>

        {/* Checkbox */}
        {!initialData?.isNew && (
          <Form.Item name="regenerate" valuePropName="checked" className="mb-6">
            <Checkbox className="text-sm">
              {t('Regenerate content of this page')}
            </Checkbox>
          </Form.Item>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <AntdCustomButton appearance="default" onClick={handleCancel}>
            {t('Cancel')}
          </AntdCustomButton>
          <AntdCustomButton appearance="important" onClick={handleOk}>
            {t('Save')}
          </AntdCustomButton>
        </div>
      </Form>
    </Modal>
  );
};

export default EditPageModal;
